/**
 * Override slots a sibling workspace (e.g. `/recap_transport`) swaps at one
 * point in the tree to avoid forking the recap table chain. Two examples:
 *
 *  - `cellRegistry` — read by {@link FlowRow}, default = recap's
 *    {@link CELL_REGISTRY}. Lets `recap_transport/` ship its own cells
 *    (sender / recipient / etc.) without recap caring.
 *  - `headerComponent` — read by {@link PreOrderSection}, default =
 *    {@link PreOrderHeader}. Lets `recap_transport/` plug in `TransportHeader`
 *    (title editor, no partner dropdown) in the per-pre-order card slot.
 *
 * The full slot set (and which recap component reads each) is documented under
 * "Sibling-workspace seams" in `recap/CLAUDE.md`. Every slot is a pure addition
 * that defaults to existing recap behavior, so `/recap` and `/purchases` are
 * unchanged when no provider is mounted. Mounted exactly once per sibling
 * workspace (by `TransportShell`).
 */
import {
  createContext,
  useContext,
  useMemo,
  type ComponentType,
  type ReactNode,
} from "react";
import type { RankedSearchConfig } from "@stockline/product-search";
import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
import { CELL_REGISTRY } from "../components/cells/registry";
import { PreOrderHeader } from "../components/PreOrderHeader";
import type { ColumnDef } from "../config/types";
import type { ProductDefaults } from "../utils/resolveProductDefaults";

export interface PreOrderHeaderProps {
  preOrder: PreOrder;
  isEditingProposal?: boolean;
  onToggleEditProposal?: () => void;
}

/**
 * Props for a `flowBatchExtraActions` override — extra controls injected into
 * the focus-view {@link FlowBatchIsland} pill. Transport supplies the batch
 * sender/recipient pickers here so ONE island carries both the shared
 * merge/split/delete actions and the transport-specific edit. Receives the
 * same `preOrder` + live `flows` the island already holds (buffered adds in,
 * staged deletes out) plus the island's `disabled` (mid-flush) flag.
 */
export interface FlowBatchExtraActionsProps {
  preOrder: PreOrder;
  flows: PreOrderFlow[];
  disabled?: boolean;
  pendingChangeCount?: number;
  isFlushing?: boolean;
  onValidate?: () => Promise<void>;
}

/** Props for an `emptyRowComponent` override (transport sender add-row). */
export interface EmptyRowOverrideProps {
  columns: ColumnDef[];
  getWidth: (id: string) => number;
  onFlowCreate: (defaults: ProductDefaults) => void;
  preOrder: PreOrder;
  partnerId?: string | null;
  /** Table is mid-flush (save/validate) — the add-row must not accept input. */
  disabled?: boolean;
}

/**
 * Swaps the list view's partner-identity column for a sibling-defined one
 * (transport surfaces the order title where sales/purchases show the partner).
 * Read by {@link PreOrderListView} (header) and {@link PreOrderRow} (per-row).
 */
export interface ListIdentityConfig {
  /** Plain column-header label rendered where the partner popover normally sits. */
  headerLabel: string;
  /** Renders the per-row identity inline (replaces the partner code + name). */
  Row: ComponentType<{ preOrder: PreOrder }>;
  /**
   * Override the group ("agence") column-header label. Default (omitted) →
   * recap/purchases render `t("ordersRecap.group")` ("Groupe"). Transport
   * passes its agence vocabulary so the same shared column reads "Agence".
   */
  groupLabel?: string;
  /** Hide the per-order `delivery_date` column (header + row cell). */
  hideDeliveryColumn?: boolean;
  /** Force the flat list (no partner grouping), ignoring the user toggle. */
  disablePartnerGrouping?: boolean;
}

/**
 * Resize config for the focus-page `FlowsTable`. A sibling workspace supplies
 * its own so it resizes against its own defaults under its own localStorage
 * key (isolated from `/recap` + `/purchases`, which share one key). All passed
 * references must be stable (module constants).
 */
export interface ColumnWidthConfig {
  storageKey: string;
  defaultWidths: Record<string, number>;
  nonResizableColumns?: ReadonlySet<string>;
}

export interface RecapPrimitivesValue {
  cellRegistry: Record<string, ComponentType<any>>;
  headerComponent: ComponentType<PreOrderHeaderProps>;
  /** Default `null` → list keeps the partner column. */
  listIdentity: ListIdentityConfig | null;
  /** Default `null` → focus view keeps `ClientSection`. */
  focusIdentityComponent: ComponentType<{
    preOrder: PreOrder;
    isLocked: boolean;
  }> | null;
  /** Default `null` → `FlowsTable` keeps the product-picker empty row. */
  emptyRowComponent: ComponentType<EmptyRowOverrideProps> | null;
  /**
   * Extra controls rendered inside the focus-view {@link FlowBatchIsland}.
   * Default `null` → recap/purchases keep the plain merge/split/delete pill.
   * Transport injects its batch sender/recipient pickers so a single island
   * carries both the shared line actions and the transport edit.
   */
  flowBatchExtraActions: ComponentType<FlowBatchExtraActionsProps> | null;
  /** Column id the new-flow auto-focus targets. Default `"quantity"`. */
  newRowFocusColId: string;
  /**
   * Hide the order-level delivery date in the focus view's
   * {@link OrderDetailsSection} (both the date pill display and the edit
   * popover's delivery field). Default `false` → sales/purchases keep the
   * "ordered · to-be-delivered" pair. Transport opts in: delivery is a
   * per-flow concern there, so an order-level delivery date is meaningless.
   */
  hideFocusDeliveryDate: boolean;
  /**
   * Replace the list header's create control. Default `null` → `RecapHeader`
   * keeps the partner-based `NewOrderDropdown`. Transport mounts its own
   * `TransportNewOrderButton` (no partner — a transport order is identified by
   * its title, created blank and edited in the focus view).
   */
  newOrderControl: ReactNode | null;
  /**
   * Override the order-level location field label in the focus view's
   * {@link OrderDetailsSection}. Default `null` → sales/purchases render
   * `t("ordersRecap.orderDetails.location")` ("Dépôt"). Transport passes an
   * already-translated string (e.g. "Agence") so the same depot selector
   * reads in the trucking domain's vocabulary without touching recap/purchases.
   */
  locationLabel: string | null;
  /**
   * Hide the order-level location/depot field in the focus view's
   * {@link OrderDetailsSection}. Default `false` → sales/purchases render the
   * depot selector. Transport sets `true`: the agence (a `group`) is selected
   * in `TransportHeader` and written to `group_id`, so the legacy
   * `location_id` depot selector is retired for that surface.
   */
  hideLocation: boolean;
  /**
   * Hide the partner-centric view toggles in the `FilterButton` popover
   * ("My clients only" + "Group by partner") and their active-filter chips.
   * Default `false` → sales/purchases keep both. Transport sets `true`: it is
   * a flat, title-based list with no partner grouping, so both toggles are
   * irrelevant — only "Show deleted only" stays.
   */
  hideClientFilters: boolean;
  /**
   * Override the focus-table column-resize config. Default `null` → recap's
   * shared defaults + `ordersRecapColumnWidths` storage key. Transport passes
   * its own so sender/recipient get a wider default share and resizes don't
   * bleed into `/recap` + `/purchases`.
   */
  columnWidthConfig: ColumnWidthConfig | null;
  /**
   * Override the list-view search config (`PreOrderListView`). Default `null`
   * → recap/purchases keep their built-in partner-name + order-number config.
   * Transport injects a config whose `textFields` + Fuse `getFn` gather
   * sender / recipient / title strings from ALL of a pre-order's flows, so a
   * transport order matches when any flow's sender or recipient matches.
   */
  listSearchConfig: RankedSearchConfig<PreOrder> | null;
  /**
   * Label for quantities whose flow carries no unit. Default `null` →
   * sales/purchases keep the `"units"` fallback in `computeFlowStats`.
   * Transport passes an already-translated `"colis"` so the footer /
   * header / list-row quantity totals read in the trucking domain's
   * vocabulary (transport lines are counted in colis, not product units).
   * Its presence also enables the per-line + header colis totals.
   */
  noUnitLabel: string | null;
}

const DEFAULT_PRIMITIVES: RecapPrimitivesValue = {
  cellRegistry: CELL_REGISTRY as Record<string, ComponentType<any>>,
  headerComponent: PreOrderHeader,
  listIdentity: null,
  focusIdentityComponent: null,
  emptyRowComponent: null,
  flowBatchExtraActions: null,
  newRowFocusColId: "quantity",
  hideFocusDeliveryDate: false,
  newOrderControl: null,
  locationLabel: null,
  hideLocation: false,
  hideClientFilters: false,
  columnWidthConfig: null,
  listSearchConfig: null,
  noUnitLabel: null,
};

const RecapPrimitivesContext =
  createContext<RecapPrimitivesValue>(DEFAULT_PRIMITIVES);

export function RecapPrimitivesProvider({
  value,
  children,
}: Readonly<{
  value: Partial<RecapPrimitivesValue>;
  children: ReactNode;
}>) {
  const resolved = useMemo<RecapPrimitivesValue>(
    () => ({
      cellRegistry: value.cellRegistry ?? DEFAULT_PRIMITIVES.cellRegistry,
      headerComponent:
        value.headerComponent ?? DEFAULT_PRIMITIVES.headerComponent,
      listIdentity: value.listIdentity ?? DEFAULT_PRIMITIVES.listIdentity,
      focusIdentityComponent:
        value.focusIdentityComponent ??
        DEFAULT_PRIMITIVES.focusIdentityComponent,
      emptyRowComponent:
        value.emptyRowComponent ?? DEFAULT_PRIMITIVES.emptyRowComponent,
      flowBatchExtraActions:
        value.flowBatchExtraActions ?? DEFAULT_PRIMITIVES.flowBatchExtraActions,
      newRowFocusColId:
        value.newRowFocusColId ?? DEFAULT_PRIMITIVES.newRowFocusColId,
      hideFocusDeliveryDate:
        value.hideFocusDeliveryDate ?? DEFAULT_PRIMITIVES.hideFocusDeliveryDate,
      newOrderControl:
        value.newOrderControl ?? DEFAULT_PRIMITIVES.newOrderControl,
      locationLabel: value.locationLabel ?? DEFAULT_PRIMITIVES.locationLabel,
      hideLocation: value.hideLocation ?? DEFAULT_PRIMITIVES.hideLocation,
      hideClientFilters:
        value.hideClientFilters ?? DEFAULT_PRIMITIVES.hideClientFilters,
      columnWidthConfig:
        value.columnWidthConfig ?? DEFAULT_PRIMITIVES.columnWidthConfig,
      listSearchConfig:
        value.listSearchConfig ?? DEFAULT_PRIMITIVES.listSearchConfig,
      noUnitLabel: value.noUnitLabel ?? DEFAULT_PRIMITIVES.noUnitLabel,
    }),
    [
      value.cellRegistry,
      value.headerComponent,
      value.listIdentity,
      value.focusIdentityComponent,
      value.emptyRowComponent,
      value.flowBatchExtraActions,
      value.newRowFocusColId,
      value.hideFocusDeliveryDate,
      value.newOrderControl,
      value.locationLabel,
      value.hideLocation,
      value.hideClientFilters,
      value.columnWidthConfig,
      value.listSearchConfig,
      value.noUnitLabel,
    ],
  );

  return (
    <RecapPrimitivesContext.Provider value={resolved}>
      {children}
    </RecapPrimitivesContext.Provider>
  );
}

export function useRecapCellRegistry(): RecapPrimitivesValue["cellRegistry"] {
  return useContext(RecapPrimitivesContext).cellRegistry;
}

export function useRecapHeaderComponent(): RecapPrimitivesValue["headerComponent"] {
  return useContext(RecapPrimitivesContext).headerComponent;
}

export function useRecapListIdentity(): RecapPrimitivesValue["listIdentity"] {
  return useContext(RecapPrimitivesContext).listIdentity;
}

export function useRecapFocusIdentity(): RecapPrimitivesValue["focusIdentityComponent"] {
  return useContext(RecapPrimitivesContext).focusIdentityComponent;
}

export function useRecapEmptyRowComponent(): RecapPrimitivesValue["emptyRowComponent"] {
  return useContext(RecapPrimitivesContext).emptyRowComponent;
}

export function useRecapFlowBatchExtraActions(): RecapPrimitivesValue["flowBatchExtraActions"] {
  return useContext(RecapPrimitivesContext).flowBatchExtraActions;
}

export function useRecapNewRowFocusColId(): RecapPrimitivesValue["newRowFocusColId"] {
  return useContext(RecapPrimitivesContext).newRowFocusColId;
}

export function useRecapHideFocusDeliveryDate(): RecapPrimitivesValue["hideFocusDeliveryDate"] {
  return useContext(RecapPrimitivesContext).hideFocusDeliveryDate;
}

export function useRecapNewOrderControl(): RecapPrimitivesValue["newOrderControl"] {
  return useContext(RecapPrimitivesContext).newOrderControl;
}

export function useRecapLocationLabel(): RecapPrimitivesValue["locationLabel"] {
  return useContext(RecapPrimitivesContext).locationLabel;
}

export function useRecapHideLocation(): RecapPrimitivesValue["hideLocation"] {
  return useContext(RecapPrimitivesContext).hideLocation;
}

export function useRecapHideClientFilters(): RecapPrimitivesValue["hideClientFilters"] {
  return useContext(RecapPrimitivesContext).hideClientFilters;
}

export function useRecapColumnWidthConfig(): RecapPrimitivesValue["columnWidthConfig"] {
  return useContext(RecapPrimitivesContext).columnWidthConfig;
}

export function useRecapListSearchConfig(): RecapPrimitivesValue["listSearchConfig"] {
  return useContext(RecapPrimitivesContext).listSearchConfig;
}

export function useRecapNoUnitLabel(): RecapPrimitivesValue["noUnitLabel"] {
  return useContext(RecapPrimitivesContext).noUnitLabel;
}
